import type { MouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Icon } from '../Icon/Icon'
import { cx } from '../utils/cx'
import type { NavItem, NavProps } from './types'
import './Nav.css'

export function useDefaultNav(): NavItem[] {
  const { t } = useTranslation()
  return [
    { id: 'home', label: t('v2.ds.nav.estancia'), icon: 'home', href: '/' },
    { id: 'booking', label: t('v2.ds.nav.reserva'), icon: 'calendar', href: '/reserva' },
    { id: 'access', label: t('v2.ds.nav.accesos'), icon: 'key', href: '/accesos' },
    { id: 'guide', label: t('v2.ds.nav.guia'), icon: 'book', href: '/guia' },
    { id: 'help', label: t('v2.ds.nav.ayuda'), icon: 'life-buoy', href: '/ayuda' },
  ]
}

function NavEntry({ item, active, onSelect, iconSize }: { item: NavItem; active: boolean; onSelect?: NavProps['onSelect']; iconSize: number }) {
  const handle = (e: MouseEvent<HTMLElement>) => onSelect?.(item.id, e)
  const inner = (
    <>
      <Icon name={item.icon} size={iconSize} strokeWidth={active ? 2.5 : 2} />
      <span className="hp-nav__label">{item.label}</span>
    </>
  )
  if (item.href) {
    return (
      <a href={item.href} onClick={handle} aria-current={active ? 'page' : undefined} className={cx('hp-nav__item', active && 'hp-nav__item--active')}>
        {inner}
      </a>
    )
  }
  return (
    <button type="button" onClick={handle} aria-current={active ? 'page' : undefined} className={cx('hp-nav__item', active && 'hp-nav__item--active')}>
      {inner}
    </button>
  )
}

/** Barra inferior de 64 px (DESIGN.md §4): cinco entradas, icono 24 + etiqueta 12/600, activa en --primary. */
export function BottomNav({ items, active = 'home', onSelect, label, className }: NavProps) {
  const { t } = useTranslation()
  const defaults = useDefaultNav()
  const list = items ?? defaults
  return (
    <nav aria-label={label ?? t('v2.ds.nav.principal')} className={cx('hp-nav hp-nav--bottom', className)} style={{ gridTemplateColumns: `repeat(${list.length}, 1fr)` }}>
      {list.map((it) => (
        <NavEntry key={it.id} item={it} active={it.id === active} onSelect={onSelect} iconSize={24} />
      ))}
    </nav>
  )
}

/** Barra lateral de 240 px en escritorio (DESIGN.md §8): mismas entradas, icono + etiqueta 16 px, activa en primary-soft. */
export function SideNav({ items, active = 'home', onSelect, label, className }: NavProps) {
  const { t } = useTranslation()
  const defaults = useDefaultNav()
  const list = items ?? defaults
  return (
    <nav aria-label={label ?? t('v2.ds.nav.principal')} className={cx('hp-nav hp-nav--side', className)}>
      {list.map((it) => (
        <NavEntry key={it.id} item={it} active={it.id === active} onSelect={onSelect} iconSize={24} />
      ))}
    </nav>
  )
}
